import { useContext } from "react";
import { ErrorDialogContext, errorHandledAsync, errorHandledSync } from "@/shared/context/error-context";
import { useShareAccounytGroupStates } from "@/features/my-data/hooks/use-share-account-group-states"
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { RegisterAccountGroupsShareService } from "@/features/accounts/account-group/services/register-account-groups-share-service"
import { GetAccountGroupsShareService } from "@/features/accounts/account-group/services/get-account-groups-share-service";
import { UpdateAccountGroupsShareService } from "@/features/accounts/account-group/services/update-account-groups-share-service";
import { MoneyLinkError, MoneyLinkException, ErrorLevel } from "@/shared/utils/custom-exception";

interface Props {
  onTapCompleteModal: () => void;
}


export const useShareAccountGroup = ({ onTapCompleteModal }: Props) => {
  const {
    ownerMembers,
    setOwnerMembers,
    shareMembers,
    setShareMembers,
    invitedMembers,
    setInvitedMembers,
    check,
    setCheck,
    invitationMail,
    setInvitationMail,
    invitationComment,
    setInvitationComment,
    isActiveTerminateButton,
    setIsActiveTerminateButton,
    isActiveInviteButton,
    setIsActiveInviteButton,
    isShowErrorDialog,
    setIsShowErrorDialog,
  } = useShareAccounytGroupStates();
  
  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);
  const registerAccountGroupsShareService = new RegisterAccountGroupsShareService();
  const getAccountGroupsShareService = new GetAccountGroupsShareService();
  const updateAccountGroupsShareService = new UpdateAccountGroupsShareService();

  // 共有終了ボタンの活性制御
  const changeTerminateButtonState = () => {
    setIsActiveTerminateButton(check);
  };

  // 招待メール送信ボタンの活性制御
  const changeSendMailButtonState = () => {
    setIsActiveInviteButton(invitationMail !== "");
  };

  // 共有メンバーの取得
  const getShareMembers = async (
    accountGroupId: string
  ): Promise<{mail: string, id: string, status: number}[]> => {
    loadingCtx.setIsLoading(true);
    try {
      const res = await getAccountGroupsShareService.getAccountGroupsShare({
        account_group_id: accountGroupId
      });
      if (res.owner_id !== undefined && res.shared_id !== undefined) {
        return [{mail: res.owner_id, id: res.shared_id, status: -1}];
      }
      else if (res.share_members) {
        return res.share_members.map((member) => ({
          mail: member.shared_owner_id,
          id: member.shared_id,
          status: member.shared_status,
        }));
      }
      else {
        return [];
      }
    } catch (error) {
      // エラーハンドリング
      console.log("getShareMembers", error);
      throw error;
      // return [];
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // 共有メンバーの更新
  const updateShareMembers = async (
    accountGroupId: string,
    sharedId: string,
    sharedStatus: number
  ) => {
    loadingCtx.setIsLoading(true);
    try {
      const res = await updateAccountGroupsShareService.updateAccountGroupsShare({
        account_group_id: accountGroupId,
        shared_id: sharedId,
        shared_status: sharedStatus,
      });
      return res;
    } catch (error) {
      // エラーハンドリング
      console.log("updateShareMembers", error);
      throw error;
      // return [];
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // メール送信処理
  const onClickSendMail = async (
    accountGroupId: string,
    invitationMail: string,
    invitationComment: string
  ) => {
    loadingCtx.setIsLoading(true);
    try {
      await registerAccountGroupsShareService.registerAccountGroupsShare({
        account_group_id: accountGroupId,
        invite_owner_id: invitationMail,
        ...(invitationComment !== "" && { message: invitationComment }),
      });
      onTapCompleteModal();
    } catch (error) {
      // エラーハンドリング
      console.log("onClickSendMail:", error);
      // 共有先ユーザーが存在しない場合は成功時と同じモーダルを表示する
      // エラーコード直書きは避けたいが，対応優先とし将来課題とする
      if (
        error instanceof MoneyLinkException &&
        (error.error.props.code === 0x20212002 || // ACCOUNTGROUPSSHARE_DBBADRECORDS03
          error.error.props.code === 0x20212003)   // ACCOUNTGROUPSSHARE_DBBADRECORDS04
      ) {
        onTapCompleteModal();
        return;
      }
      // バリデーションエラー(webmoneylink_parameter_error)時は専用メッセージに差し替える
      if (
        error instanceof MoneyLinkException &&
        error.error.props.reason === "webmoneylink_parameter_error"
      ) {
        throw new MoneyLinkException({
          error: new MoneyLinkError({
            level: ErrorLevel.Warn,
            code: error.error.props.code,
            reason: error.error.props.reason,
            messageTitle: undefined,
            messageBody:
              "メッセージに使用できない文字が含まれているか、文字数が上限を超えています。\n入力内容をご確認ください。",
          }),
        });
      }
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };


  return {
    ownerMembers,
    setOwnerMembers,
    shareMembers,
    setShareMembers,
    invitedMembers,
    setInvitedMembers,
    check,
    setCheck,
    invitationMail,
    setInvitationMail,
    invitationComment,
    setInvitationComment,
    isActiveTerminateButton,
    setIsActiveTerminateButton,
    isActiveInviteButton,
    setIsActiveInviteButton,
    isShowErrorDialog,
    setIsShowErrorDialog,
    getShareMembers: errorHandledAsync(getShareMembers, setError),
    updateShareMembers: errorHandledAsync(updateShareMembers, setError),
    onClickSendMail: errorHandledAsync(onClickSendMail, setError),
    changeTerminateButtonState: errorHandledSync(changeTerminateButtonState, setError),
    changeSendMailButtonState: errorHandledSync(changeSendMailButtonState, setError),
  };
};
